import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import type { FloatingDockItem } from '../ui/floating-dock';
import { resolveExperienceMode, type ExperienceMode } from './core/resolveExperienceMode';
import {
  dockShowsBookShell,
  dockShowsLanguageShell,
  dockShowsScrollShell,
  dockShowsVumShell,
  dockUsesCompactBottomInset,
} from './core/webdesignedDockPolicy';
import { useLanguage } from '../../contexts/useLanguage';
import { useAuth } from '../../contexts/AuthContext';
import { useWebDesignedCart } from '../../contexts/WebDesignedCartContext';
import type { Language } from '../../contexts/languageContextDef';
import { buildWebDesignedDockItems } from './webDesignedDockItems';

const LANGUAGE_CYCLE: Language[] = ['fr', 'en', 'zh'];

export type DockLayoutMode = 'auto' | 'mobile' | 'pad' | 'desktop';

/** When not `auto`, lock experience mode from the forced layout shell. */
function forcedExperienceMode(layout: DockLayoutMode): ExperienceMode | null {
  if (layout === 'mobile') return 'mobile';
  if (layout === 'pad') return 'pad';
  if (layout === 'desktop') return 'desktop';
  return null;
}

export function useWebDesignedFloatingDock(forcedLayoutMode: DockLayoutMode = 'auto'): {
  items: FloatingDockItem[];
  /**
   * Safe-area dock bottom on mobile + pad only (see `dockUsesCompactBottomInset`).
   * Separate from whether VUM / Book / language tiles appear (`webdesignedDockPolicy`).
   */
  isCompactShell: boolean;
  /** Active shell — single source alongside `resolveExperienceMode`. */
  experienceMode: ExperienceMode;
} {
  const { t, language, setLanguage } = useLanguage();
  const { isSignedIn, user: appUser, role, logout } = useAuth();
  const cart = useWebDesignedCart();
  const location = useLocation();
  const navigate = useNavigate();

  const cycleLanguage = useCallback(() => {
    const i = LANGUAGE_CYCLE.indexOf(language);
    setLanguage(LANGUAGE_CYCLE[(i + 1) % LANGUAGE_CYCLE.length]);
  }, [language, setLanguage]);

  const [experienceMode, setExperienceMode] = useState<ExperienceMode>(() => {
    const forced = forcedExperienceMode(forcedLayoutMode);
    if (forced) return forced;
    if (typeof window === 'undefined') return 'desktop';
    return resolveExperienceMode(window.innerWidth);
  });

  useEffect(() => {
    const forced = forcedExperienceMode(forcedLayoutMode);
    if (forced) {
      setExperienceMode(forced);
      return;
    }
    const sync = () => setExperienceMode(resolveExperienceMode(window.innerWidth));
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, [forcedLayoutMode]);

  const includeVumInDock = dockShowsVumShell(experienceMode);
  const includeLanguageInDock = dockShowsLanguageShell(experienceMode);
  const includeBookInDock = dockShowsBookShell(experienceMode);
  const includeScrollInDock = dockShowsScrollShell(experienceMode);
  const compactDockBottom = dockUsesCompactBottomInset(experienceMode);

  const pathname = location.pathname;

  const items = useMemo(() => {
    return buildWebDesignedDockItems({
      pathname,
      t,
      cart,
      navigate,
      isSignedIn,
      role,
      includeVumInDock,
      includeLanguageInDock,
      includeScrollInDock,
      includeBookInDock,
      language,
      cycleLanguage,
      setLanguage,
      useMobileLanguagePicker: true,
      // Mobile + pad: rich account (portal + logout). Desktop uses header account controls.
      mobileSignedInAccountDock: experienceMode !== 'desktop',
      appUser,
      logout,
    });
  }, [
    pathname,
    t,
    cart,
    navigate,
    isSignedIn,
    role,
    includeVumInDock,
    includeLanguageInDock,
    includeScrollInDock,
    includeBookInDock,
    language,
    cycleLanguage,
    setLanguage,
    experienceMode,
    appUser,
    logout,
  ]);

  return { items, isCompactShell: compactDockBottom, experienceMode };
}
